
<template>
    <div
        class="automation-element-list"
        :class="{ collapsed: !isOpen }"
        @keyup.esc="close"
    >
        <div class="sidebar-header">
            <template v-if="sectionSelected.id === $options.SECTIONS.NONE">
                <DsIconButton name="x" @click="close" />
                <span class="sidebar-header-title">
                    {{ $t('funnel.findElement.title') }}
                </span>
            </template>
            <template v-else-if="sectionSelected.id === $options.SECTIONS.STATUS && statusSectionSelected.id !== $options.STATUS_SECTIONS.NONE">
                <DsIconButton name="arrow-left" @click="statusSectionSelected = { id: $options.STATUS_SECTIONS.NONE }" />

                <span class="sidebar-header-title">
                    {{ $t(statusSectionSelected.label) }}
                </span>
            </template>
            <template v-else>
                <DsIconButton name="arrow-left" @click="sectionSelected = { id: $options.SECTIONS.NONE }" />

                <span class="sidebar-header-title">
                    {{ $t(sectionSelected.label) }}
                </span>
            </template>
        </div>

        <DsInlineAlert v-if="!hasElements" type="info" class="no-elements">
            {{ $t('funnel.element.list.modal.noElements') }}
        </DsInlineAlert>

        <template v-else>
            <DsInputField
                v-model="searchString"
                type="search"
                class="search-input"
                data-qa="search-input"
                :placeholder="$t('funnel.element.list.modal.searchPlaceholder')"
            >
                <template #leading>
                    <DsIcon name="search" />
                </template>
            </DsInputField>

            <div v-if="!filteredElements.length && searchString" class="search-not-found">
                {{ $t('funnel.element.list.modal.filteredElements.search', { searchString }) }}
            </div>

            <div v-else-if="!filteredElements.length" class="search-not-found">
                {{ $t('funnel.element.list.modal.filteredElements') }}
            </div>

            <DisplayList v-if="searchString" :elements="filteredElements" @click="onClickElement" />

            <template v-else-if="sectionSelected.id === $options.SECTIONS.NONE">
                <DsListItem
                    v-for="section in sections"
                    :key="section.id"
                    :description="$tc('funnel.findElement.count', section.elements.length, { count: section.elements.length })"
                    :title="$t(section.label)"
                    trailing-icon="arrow-right"
                    clickable
                    @click="selectSection(section)"
                >
                    <template #trailing>
                        <DsIcon name="chevron-right" />
                    </template>
                </DsListItem>
            </template>

            <template v-else-if="sectionSelected.id === $options.SECTIONS.STATUS && statusSectionSelected.id === $options.STATUS_SECTIONS.NONE">
                <DsListItem
                    v-for="section in statusSections"
                    :key="section.id"
                    :title="$t(section.label)"
                    :description="$tc('funnel.findElement.count', section.elements.length, { count: section.elements.length })"
                    trailing-icon="arrow-right"
                    clickable
                    @click="selectStatus(section)"
                >
                    <template #trailing>
                        <DsIcon name="chevron-right" />
                    </template>
                </DsListItem>
            </template>

            <DisplayList v-else :elements="filteredElements" @click="onClickElement" />
        </template>
    </div>
</template>

<script>
import { getIconPathByCellType } from '../../utils/cell/automation-display-utils';
import {
    NOTES_TYPE,
    EDGE_TYPE,
    FLOW_TYPE,
    SEQUENCE_START,
    DECISION_TYPE,
    INTEGRATION_TRIGGER_TYPE,
    INTEGRATION_ACTION_TYPE,
    CELL_TOOLTIP,
    GOALS,
} from '../../utils/editor.constants';

import DisplayList from './DisplayList.vue';

const SECTIONS = {
    NONE: 'none',
    WHEN: 'when',
    THEN: 'then',
    NOTES: 'notes',
    DECISION: 'decision',
    STATUS: 'status',
};

const STATUS_SECTIONS = {
    NONE: 'status_none',
    ERROR: 'error',
    SETUP_REQUIRED: 'setupRequired',
    PUBLISHED: 'published',
    UNPUBLISHED: 'unpublished',
};

export default {
    components: {
        DisplayList,
    },
    props: {
        isOpen: Boolean,
        cells: {
            type: Array,
            default: () => ([]),
        },
        publishedCells: {
            type: Array,
            default: () => ([]),
        },
        enabledIntegrations: {
            type: Array,
            default: () => ([]),
        },
    },
    SECTIONS,
    STATUS_SECTIONS,
    emits: ['close-element-list', 'set-sequence-id', 'open-config-modal', 'open-note'],
    data() {
        return {
            searchString: '',
            sectionSelected: { id: SECTIONS.NONE },
            statusSectionSelected: { id: STATUS_SECTIONS.NONE },
        };
    },
    computed: {
        hasElements() { return this.cells.length > 2; },

        filteredElements() {
            let elems = [];

            if (this.sectionSelected.id === SECTIONS.NONE) {
                elems = this.allElements;
            } else if (this.statusSectionSelected.id !== STATUS_SECTIONS.NONE) {
                elems = this.statusSectionSelected.elements;
            } else {
                elems = this.sectionSelected.elements;
            }

            return elems.filter(({ searchInfo }) => {
                return searchInfo.toLowerCase().includes(this.searchString.toLowerCase());
            });
        },

        sections() {
            return Object.values(SECTIONS).map((id) => {
                const elements = this.elementsBySection(id);

                return {
                    id,
                    label: this.$t(`funnel.findElement.${id}`),
                    elements,
                };
            }).filter((section) => {
                return section.id !== SECTIONS.NONE;
            });
        },

        statusSections() {
            return Object.values(STATUS_SECTIONS).map((id) => {
                const elements = this.elementsByStatusSection(id);

                return {
                    id,
                    label: this.$t(`funnel.findElement.status.${id}`),
                    elements,
                };
            }).filter((section) => {
                return section.id !== STATUS_SECTIONS.NONE;
            });
        },

        allElements() {
            return this.cells.map((cell) => {
                const displayLabel = this.getCellTypeLabel(cell);
                const status = this.getStatus(cell);
                let name = cell.value?.name;
                let searchInfo = [cell.value?.name, displayLabel, this.$t(status)].join(' ');

                if (cell.type === NOTES_TYPE) {
                    name = cell.value?.notes.replace(/<[^>]*>/g, ''); // remove html tags
                    searchInfo = [cell.value?.name, displayLabel, name].join(' ');
                }

                const isWhen = this.isWhen(cell.type);
                const isThen = !isWhen && cell.type !== DECISION_TYPE && cell.type !== NOTES_TYPE && cell.type !== SEQUENCE_START;

                return {
                    cell,
                    searchInfo,
                    name,
                    displayLabel,
                    status: this.$t(status),
                    isWhen,
                    isThen,
                    isBroken: this.isBroken(cell),
                    iconSrc: getIconPathByCellType(cell.type),
                    setupRequired: status === CELL_TOOLTIP.elementSetupRequiredShort,
                    isPublished: status === CELL_TOOLTIP.elementPublished,
                    isUnpublishedChanges: status === CELL_TOOLTIP.elementPublishedWithChanges,
                };
            }).filter(({ cell }) => {
                if ([EDGE_TYPE].includes(cell.type)) return false;
                if (parseInt(cell.meta.id, 10) < 2) return false;

                return true;
            });
        },
    },
    methods: {
        selectStatus(section) {
            this.statusSectionSelected = section;
        },

        selectSection(section) {
            this.sectionSelected = section;
        },

        close() {
            this.sectionSelected = { id: SECTIONS.NONE };
            this.statusSectionSelected = { id: STATUS_SECTIONS.NONE };
            this.$emit('close-element-list');
        },

        getIconPathByCellType,

        getCellTypeLabel(cardCell) {
            switch (cardCell.type) {
            case INTEGRATION_TRIGGER_TYPE:
                return this.enabledIntegrations.find((integration) => {
                    return cardCell.value.integrationTriggerId === integration.id;
                })?.name;
            case INTEGRATION_ACTION_TYPE:
                return this.enabledIntegrations.find((integration) => {
                    return cardCell.value.integrationActionId === integration.id;
                })?.name;
            case FLOW_TYPE:
                return this.$t('funnel.edit.sideBar.template.flow');
            default:
                return this.getTypeLabel(cardCell.type);
            }
        },

        getTypeLabel(type) {
            const isWhen = this.$te(`funnel.sidebar.when.cell.${type}.label`);
            const isThen = this.$te(`funnel.sidebar.then.cell.${type}.label`);
            const isOther = this.$te(`funnel.type.${type}.label`);
            const cellTypeLabel = isWhen ? `funnel.sidebar.when.cell.${type}.label`
                : isThen ? `funnel.sidebar.then.cell.${type}.label`
                    : isOther ? `funnel.type.${type}.label` : type;

            return this.$t(cellTypeLabel);
        },

        elementsBySection(sectionId) {
            switch (sectionId) {
            case SECTIONS.WHEN:
                return this.allElements.filter(({ isWhen }) => isWhen);
            case SECTIONS.THEN:
                return this.allElements.filter(({ isThen }) => isThen);
            case SECTIONS.NOTES:
                return this.allElements.filter(({ cell }) => cell.type === NOTES_TYPE);
            case SECTIONS.DECISION:
                return this.allElements.filter(({ cell }) => cell.type === DECISION_TYPE);
            case SECTIONS.STATUS:
                return this.allElements.filter(({ isBroken, setupRequired, isPublished, isUnpublishedChanges }) => {
                    return isBroken || setupRequired || isPublished || isUnpublishedChanges;
                });
            default:
                return [];
            }
        },

        elementsByStatusSection(sectionId) {
            switch (sectionId) {
            case STATUS_SECTIONS.ERROR:
                return this.allElements.filter(({ isBroken }) => isBroken);
            case STATUS_SECTIONS.SETUP_REQUIRED:
                return this.allElements.filter(({ setupRequired }) => setupRequired);
            case STATUS_SECTIONS.PUBLISHED:
                return this.allElements.filter(({ isPublished }) => isPublished);
            case STATUS_SECTIONS.UNPUBLISHED:
                return this.allElements.filter(({ isUnpublishedChanges }) => isUnpublishedChanges);
            default:
                return [];
            }
        },

        onClickElement(cell) {
            if (cell.type === FLOW_TYPE) {
                this.$emit('set-sequence-id', cell.meta.id);
            } else if (cell.meta.parent !== '1') {
                this.$emit('set-sequence-id', cell.meta.parent);
                this.openCell(cell);
            } else {
                this.openCell(cell);
            }
        },

        openCell(cell) {
            if (cell.type === NOTES_TYPE) {
                this.$emit('open-note', cell);
            } else {
                this.$emit('open-config-modal', cell);
            }

            this.close();
        },

        getStatus(cell) {
            if (cell.type === NOTES_TYPE) {
                return '';
            }

            if (this.isBroken(cell)) {
                return CELL_TOOLTIP.elementBroken;
            }

            if (this.publishedCells.length) {
                if (this.isPublished(cell)) {
                    return CELL_TOOLTIP.elementPublished;
                }

                return CELL_TOOLTIP.elementPublishedWithChanges;
            }

            if (this.isReady(cell)) {
                return CELL_TOOLTIP.elementReadyShort;
            }

            return CELL_TOOLTIP.elementSetupRequiredShort;
        },

        cellHasBeenPublished(cell) {
            return !!this.publishedCells.find((publishedCell) => publishedCell.id === cell.id);
        },

        isBroken(cell) {
            return cell?.value?.broken === '1';
        },

        isReady(cell) {
            return cell?.value?.ready === '1';
        },

        isPublished(cell) {
            return cell?.value?.published === '1';
        },

        isWhen(type) {
            return [...Object.values(GOALS), INTEGRATION_TRIGGER_TYPE].includes(type);
        },
    },
};
</script>

<style lang="scss" scoped>
$shortcut-sidebar-width: 320px;

.automation-element-list {
    --input-margin-bottom: 0;
    font-size: $font-size-xs;
    width: $shortcut-sidebar-width;
    height: 100%;
    border: solid 1px $color-gray-200;
    background-color: $color-paper;

    transition: all 0.5s ease-out;

    position: fixed;
    right: 0;
    top: 0;
    z-index: 50;
    white-space: nowrap;

    &.collapsed {
        width: 0px;
        border: none;
    }
}

.sidebar-header {
    display: flex;
    align-items: center;
    padding: $spacing-100;
    width: $shortcut-sidebar-width;
    border-bottom: 1px solid $color-gray-300;

    .sidebar-header-title {
        font-size: $font-size-lg;
    }
}

.search-input {
    margin: $spacing-200;
    width: calc($shortcut-sidebar-width - $spacing-400);
}

.search-not-found {
    margin: $spacing-200;
    color: $color-text-subtle;
}

.no-elements {
    margin: $spacing-200;
}
</style>
